/**
 * **Los materiales y sus precios: alta, corrección, baja y descarga.**
 *
 * Es el único tema que crea una fila en **dos pasos** —el material y luego su precio, que puede
 * llegar otro día con la factura—, y todo lo de aquí refresca lo mismo: la lista de materiales y la
 * estructura de costos y las fórmulas y Lisa que usa el cotizador. Ese refresco compartido es
 * `useRefreshMaterials`, y repartir estas consultas habría sido repartirlo a él.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiDownload, apiGet, apiPatch, apiPost } from "@/lib/api";
import { pick } from "@/lib/undo";

import { keys } from "./claves";
import { cached, useUndoable } from "./deshacer";

import type { Category, Material, NewMaterial, PriceEntry, Unit } from "../types";

// ── Insumos ───────────────────────────────────────────────
export const useCategories = () =>
  useQuery({ queryKey: keys.categories, queryFn: () => apiGet<Category[]>("/materials/categories"), staleTime: Infinity });

export const useMaterials = (enabled = true) => useQuery({ queryKey: keys.materials, queryFn: () => apiGet<Material[]>("/materials"), enabled });

export const usePriceHistory = (id: string | null) =>
  useQuery({
    queryKey: keys.prices(id ?? ""),
    queryFn: () => apiGet<PriceEntry[]>(`/materials/${id}/prices`),
    enabled: id !== null,
  });

/** Sin `package_price` se registra «sin precio» desde esa fecha. */
export type NewPrice = { package_quantity?: number; package_price?: number; effective_from: string; note?: string };

function useRefreshMaterials() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: keys.materials }),
      client.invalidateQueries({ queryKey: keys.structure }),
      client.invalidateQueries({ queryKey: ["cost-base", "formulas"] }),
      client.invalidateQueries({ queryKey: ["cost-base", "lisa"] }),
    ]);
}

/**
 * Crea una materia prima.
 *
 * **Sin precio también se crea**, y eso no es un descuido: el material existe el día que
 * entra al almacén y el precio llega con la factura, que puede ser otro día. Lo que no puede
 * pasar es que se cree sin precio y nadie lo diga — sale marcado «sin precio» y no suma al
 * costo hasta que lo tenga.
 */
export function useCreateMaterial() {
  const refresh = useRefreshMaterials();
  return useMutation({
    mutationFn: (body: NewMaterial) => apiPost<Material>("/materials", body),
    // La lista se refresca para que aparezca enseguida en el buscador de «agregar material»
    // del editor de mezcla, que es justo para lo que se está creando.
    onSuccess: refresh,
  });
}

/**
 * Registra el precio de un material **recién creado**.
 *
 * Existe aparte de `useAddPrice` porque aquella ata el identificador al montarse, y aquí el
 * identificador no existe hasta que el material se ha creado: el alta son dos pasos y el
 * segundo necesita lo que devolvió el primero.
 */
export function useAddPriceTo() {
  const refresh = useRefreshMaterials();
  return useMutation({
    mutationFn: ({ id, ...body }: NewPrice & { id: string }) =>
      apiPost<PriceEntry>(`/materials/${id}/prices`, body),
    onSuccess: refresh,
  });
}

export function useAddPrice(id: string) {
  const client = useQueryClient();
  const post = (body: NewPrice) => apiPost<PriceEntry>(`/materials/${id}/prices`, body);
  return useUndoable(
    post,
    (body) => {
      const material = cached<Material>(client, keys.materials, id);
      if (!material) return null;
      const before = material.price;
      // Deshacer registra otra vez lo que había: la historia guarda también la corrección.
      const restore: NewPrice = before
        ? { package_quantity: before.package_quantity, package_price: before.package_price, effective_from: body.effective_from }
        : { effective_from: body.effective_from };
      return { label: `Precio de ${material.name}`, undo: () => post(restore) };
    },
    useRefreshMaterials(),
  );
}

export type MaterialChanges = Partial<{
  name: string;
  category_code: string;
  unit: Unit;
  supplier_name: string | null;
  siigo_code: string | null;
  is_active: boolean;
  review_note: string | null;
}> &
  Partial<Pick<Material, "cost_element" | "classification_confirmed" | "group_name">>;

export function useUpdateMaterial(id: string) {
  const client = useQueryClient();
  const patch = (body: MaterialChanges) => apiPatch<Material>(`/materials/${id}`, body);
  return useUndoable(
    patch,
    (body) => {
      const material = cached<Material>(client, keys.materials, id);
      if (!material) return null;
      const before = pick(material, Object.keys(body)) as MaterialChanges;
      if ("group_name" in before && before.group_name === null) before.group_name = "";
      return { label: `${"group_name" in body ? "Grupo" : "Cambio"} de ${material.name}`, undo: () => patch(before) };
    },
    useRefreshMaterials(),
  );
}

export function useDeleteMaterial(id: string) {
  const refresh = useRefreshMaterials();
  return useMutation({ mutationFn: () => apiDelete(`/materials/${id}`), onSuccess: refresh });
}

/** Descarga en Excel las materias primas indicadas, en ese orden. */
export async function exportMaterials(ids: string[]) {
  const { blob, filename } = await apiDownload("/materials/export", { ids });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
